import dayjs from 'dayjs'
import ThermalPrinterBase from '../../services/thermalPrinter/ThermalPrinterBase'
import { useFormatNumber } from '~/composables/useUtils'
import { isMoneyMasked, MASKED_MONEY } from '~/composables/useDashboardMoneyMask'
import { grossSubtotalFromReceipt, aggregateReceiptVatSplit } from '~/utils/receiptDetailModal'
import { useSettingsStore } from '~/stores/settings'

export default class ThermalPrintOrderSummary extends ThermalPrinterBase {
  printOrderSummary(payload: any) {
    this.print_data = this.withPayload(payload)
    return this
  }

  withPayload(payload: any) {
    const receipts = Array.isArray(payload?.receipts) ? payload.receipts : []
    const goodBills = receipts.filter((r: any) => !r?.IsDeleted)
    const voidBills = receipts.filter((r: any) => r?.IsDeleted)

    const customers = goodBills.reduce((sum: number, r: any) => sum + Number(r?.CustomerCount || 0), 0)
    // "Total" = GROSS (before item discount), robust across receipt versions (Σ extendedPrice).
    const subTotal = goodBills.reduce((sum: number, r: any) => sum + grossSubtotalFromReceipt(r), 0)
    const itemsDiscount = goodBills.reduce((sum: number, r: any) => sum + Number(r?.ItemDiscount || 0), 0)
    const service = goodBills.reduce((sum: number, r: any) => sum + Number(r?.ServiceCharge || 0), 0)
    const discountBill = goodBills.reduce((sum: number, r: any) => sum + Number(r?.Discount || 0), 0)
    // Recompute VAT fresh (version-aware) — consistent with the server + other summaries.
    // Split into Exclusive (added on top, positive) and Inclusive (embedded, deduction/
    // negative); a mixed day has both, so they print as separate lines.
    const vatSplit = aggregateReceiptVatSplit(goodBills)
    const grandTotal = goodBills.reduce((sum: number, r: any) => sum + Number(r?.GrandTotal || 0), 0)
    const voidedAmount = voidBills.reduce((sum: number, r: any) => sum + Number(r?.GrandTotal || 0), 0)

    const byPaymentType = this.aggregatePaymentType(goodBills)
    const byShift = this.aggregateShift(goodBills)
    const byCashier = this.aggregateCashier(goodBills)

    return [
      { name: 'PrintTitle', value: payload?.store?.name || '-' },
      { name: 'LineFeed', value: true },
      { name: 'PrintTitle', value: '(BACK DATE REPORT)' },
      { name: 'LineFeed', value: true },
      { name: 'Report', value: 'Summary Income' },
      { name: 'Date', value: payload?.reportDateText || dayjs().format('D MMMM YYYY') },
      { name: 'Time', value: payload?.reportTimeText || 'all day' },
      { name: 'LineFeed', value: true },
      { name: 'PrintLine', value: true },
      { name: 'LineFeed', value: true },
      { name: 'PrintTitle', value: `Summary (${goodBills.length} bills)` },
      { name: 'LineFeed', value: true },
      // Guest count only when Customer mode is enabled.
      ...(useSettingsStore().enableCustomerMode
        ? [{ name: 'Customer ', value: customers }, { name: 'LineFeed', value: true }]
        : []),
      { name: 'Total', value: this.amount(subTotal) },
      { name: 'Discount Items', value: this.amount(itemsDiscount) },
      { name: 'Service', value: this.amount(service) },
      { name: 'Discount Bill', value: this.amount(discountBill) },
      ...(vatSplit.exclusive ? [{ name: 'VAT (Exclusive)', value: this.amount(vatSplit.exclusive) }] : []),
      ...(vatSplit.inclusive ? [{ name: 'VAT (Inclusive)', value: this.amount(vatSplit.inclusive) }] : []),
      { name: 'PrintLine', value: true },
      { name: 'Grand Total', value: `*** ${this.amount(grandTotal)}` },
      { name: 'PrintDoubleLine', value: true },
      { name: 'LineFeed', value: true },
      { name: 'PrintComment', value: `Voided ${voidBills.length} bills,${this.amount(voidedAmount)}` },
      { name: 'LineFeed', value: true },
      { name: 'PrintTitle', value: 'By Payment type' },
      ...byPaymentType,
      { name: 'LineFeed', value: true },
      { name: 'PrintTitle', value: 'By shift' },
      ...byShift,
      { name: 'LineFeed', value: true },
      { name: 'PrintTitle', value: 'By cashier' },
      ...byCashier,
      { name: 'LineFeed', value: true },
      { name: 'PrintLine', value: true },
      { name: 'LineFeed', value: true },
      { name: 'PrintComment', value: 'Back Date Report' },
      { name: 'PrintComment', value: `Print at ${dayjs().format('HH:mm:ss D MMMM YYYY')}` },
      { name: 'LineFeed', value: true },
      { name: 'PrintComment', value: '****** BACK DATE REPORT *****' },
      { name: 'LineFeed', value: true },
      { name: 'LineFeed', value: true },
      { name: 'PrintLine', value: true },
      { name: 'LineFeed', value: true },
    ]
  }

  private amount(value: number) {
    if (isMoneyMasked()) return `   ${MASKED_MONEY}`
    return `   ${useFormatNumber(Number(value || 0), 2, true)}`
  }

  private aggregatePaymentType(receipts: any[]) {
    const map = new Map<string, { total: number; count: number }>()

    for (const receipt of receipts) {
      const lines = Array.isArray(receipt?.multiplePayment) && receipt.multiplePayment.length
        ? receipt.multiplePayment.map((p: any) => ({
            name: String(p?.name || p?.type || 'Unknown'),
            amount: Number(p?.amount || p?.inputAmount || 0),
          }))
        : [{ name: String(receipt?.PaymentType || 'Unknown'), amount: Number(receipt?.GrandTotal || 0) }]

      for (const line of lines) {
        const row = map.get(line.name) || { total: 0, count: 0 }
        row.total += line.amount
        row.count += 1
        map.set(line.name, row)
      }
    }

    return Array.from(map.entries()).map(([name, row]) => ({
      name: `${name} (${row.count})`,
      value: this.amount(row.total),
    }))
  }

  private aggregateShift(receipts: any[]) {
    const map = new Map<string, number>()
    for (const receipt of receipts) {
      const shift = String(receipt?.ShiftWork || '-')
      map.set(shift, (map.get(shift) || 0) + Number(receipt?.GrandTotal || 0))
    }
    return Array.from(map.entries()).map(([shift, total]) => ({
      name: `Shift ${shift}`,
      value: this.amount(total),
    }))
  }

  private aggregateCashier(receipts: any[]) {
    const map = new Map<string, number>()
    for (const receipt of receipts) {
      const cashier = String(receipt?.User || receipt?.Cashier || '-')
      map.set(cashier, (map.get(cashier) || 0) + Number(receipt?.GrandTotal || 0))
    }
    return Array.from(map.entries()).map(([cashier, total]) => ({
      name: cashier,
      value: this.amount(total),
    }))
  }
}
